import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Skeleton,
} from "@mui/material";
import { CheckCircle, Schedule, Event, Cancel } from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BASE_URL } from "../../../services/configUrls";

const TrainerStatusCards = () => {
  const [fdpData, setFdpData] = useState({
    completed: 0,
    ongoing: 0,
    upcoming: 0,
    canceled: 0,
  });
  const [edpData, setEdpData] = useState({
    completed: 0,
    ongoing: 0,
    upcoming: 0,
    canceled: 0,
  });
  const [techCampData, setTechCampData] = useState({
    completed: 0,
    ongoing: 0,
    upcoming: 0,
    canceled: 0,
  });
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEventSummary = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");

        if (!accessToken) {
          throw new Error("Access token not found");
        }

        const response = await fetch(
          `${BASE_URL}/event/trainer/event-summary`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Update state with API data
        if (data.event_summary?.fdp) {
          setFdpData({
            completed: data.event_summary.fdp.completed,
            ongoing: data.event_summary.fdp.ongoing,
            upcoming: data.event_summary.fdp.upcoming,
            canceled: data.event_summary.fdp.canceled,
          });
        }

        // if (data.event_summary?.edp) {
        //   setEdpData({
        //     completed: data.event_summary.edp.completed,
        //     ongoing: data.event_summary.edp.ongoing,
        //     upcoming: data.event_summary.edp.upcoming,
        //     canceled: data.event_summary.edp.canceled,
        //   });
        // }

        if (data.event_summary?.tech_camp) {
          setTechCampData({
            completed: data.event_summary.tech_camp.completed,
            ongoing: data.event_summary.tech_camp.ongoing,
            upcoming: data.event_summary.tech_camp.upcoming,
            canceled: data.event_summary.tech_camp.canceled,
          });
        }

        // Transform availability_summary data for the chart
        if (data.availability_summary) {
          const chartData = Object.entries(data.availability_summary).map(
            ([month, stats]) => ({
              month: month,
              available: stats.available.count,
              booked: stats.booked.count,
              pending: stats.pending.count,
            })
          );
          setAvailabilityData(chartData);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching event summary:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchEventSummary();
  }, []);

  const trainerFdpData = [
    {
      label: "Upcoming",
      count: fdpData.upcoming,
      icon: <Event sx={{ fontSize: 20 }} />,
      color: "#ff9800",
      bgColor: "#fff3e0",
    },
    {
      label: "Ongoing",
      count: fdpData.ongoing,
      icon: <Schedule sx={{ fontSize: 20 }} />,
      color: "#2196f3",
      bgColor: "#e3f2fd",
    },
    {
      label: "Completed",
      count: fdpData.completed,
      icon: <CheckCircle sx={{ fontSize: 20 }} />,
      color: "#4caf50",
      bgColor: "#e8f5e9",
    },
    {
      label: "Canceled",
      count: fdpData.canceled,
      icon: <Cancel sx={{ fontSize: 20 }} />,
      color: "#f44336",
      bgColor: "#ffebee",
    },
  ];

  const trainerEdpData = [
    {
      label: "Upcoming",
      count: edpData.upcoming,
      icon: <Event sx={{ fontSize: 20 }} />,
      color: "#ff9800",
      bgColor: "#fff3e0",
    },
    {
      label: "Ongoing",
      count: edpData.ongoing,
      icon: <Schedule sx={{ fontSize: 20 }} />,
      color: "#2196f3",
      bgColor: "#e3f2fd",
    },
    {
      label: "Completed",
      count: edpData.completed,
      icon: <CheckCircle sx={{ fontSize: 20 }} />,
      color: "#4caf50",
      bgColor: "#e8f5e9",
    },
    {
      label: "Canceled",
      count: edpData.canceled,
      icon: <Cancel sx={{ fontSize: 20 }} />,
      color: "#f44336",
      bgColor: "#ffebee",
    },
  ];

  const trainerTechCampData = [
    {
      label: "Upcoming",
      count: techCampData.upcoming,
      icon: <Event sx={{ fontSize: 20 }} />,
      color: "#ff9800",
      bgColor: "#fff3e0",
    },
    {
      label: "Ongoing",
      count: techCampData.ongoing,
      icon: <Schedule sx={{ fontSize: 20 }} />,
      color: "#2196f3",
      bgColor: "#e3f2fd",
    },
    {
      label: "Completed",
      count: techCampData.completed,
      icon: <CheckCircle sx={{ fontSize: 20 }} />,
      color: "#4caf50",
      bgColor: "#e8f5e9",
    },
    {
      label: "Canceled",
      count: techCampData.canceled,
      icon: <Cancel sx={{ fontSize: 20 }} />,
      color: "#f44336",
      bgColor: "#ffebee",
    },
  ];

  const TrainerStatusCard = ({ title, data }) => (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1rem" }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              ml: 0.75,
              width: 18,
              height: 18,
              borderRadius: "50%",
              border: "2px solid #5e6ad2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#5e6ad2",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            i
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          {data.map((item, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: item.bgColor,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  backgroundColor: item.color,
                  borderRadius: 1,
                  p: 0.75,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  minWidth: 36,
                  minHeight: 36,
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1a1a1a",
                    fontSize: "1.25rem",
                    lineHeight: 1.2,
                  }}
                >
                  {item.count}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const SkeletonCard = () => (
    <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Skeleton variant="text" width={120} height={24} />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          {[1, 2, 3, 4].map((item) => (
            <Paper
              key={item}
              elevation={0}
              sx={{
                p: 1.5,
                backgroundColor: "#f5f5f5",
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Skeleton variant="rounded" width={36} height={36} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={40} height={28} />
                <Skeleton variant="text" width={60} height={16} />
              </Box>
            </Paper>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <SkeletonCard />
          <SkeletonCard />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Skeleton variant="text" width={150} height={24} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={220} />
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <TrainerStatusCard title="FDP Status" data={trainerFdpData} />
        {/* <TrainerStatusCard title="EDP Status" data={trainerEdpData} /> */}
        <TrainerStatusCard
          title="Tech Camp Status"
          data={trainerTechCampData}
        />
      </Box>

      {/* Availability Summary Bar Chart - Compact Version */}
      {availabilityData.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "#1a1a1a", fontSize: "1rem" }}
                >
                  Availability Summary
                </Typography>
                <Box
                  sx={{
                    ml: 0.75,
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "2px solid #5e6ad2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#5e6ad2",
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  i
                </Box>
              </Box>

              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={availabilityData}
                  margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
                  barGap={2}
                  barCategoryGap={25}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                  />
                  <YAxis
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      fontSize: "12px",
                    }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: "5px", fontSize: "11px" }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Bar
                    dataKey="available"
                    fill="#4caf50"
                    name="Available"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={20}
                  />
                  <Bar
                    dataKey="booked"
                    fill="#2196f3"
                    name="Booked"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={20}
                  />
                  <Bar
                    dataKey="pending"
                    fill="#ff9800"
                    name="Pending"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default TrainerStatusCards;
